import {
  Box,
  Typography,
  styled,
  TextField,
  Button,
  keyframes,
  BoxProps,
} from "@mui/material";

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
    0% { transform: scale(1); opacity: 0.4; }
    50% { transform: scale(1.05); opacity: 0.6; }
    100% { transform: scale(1); opacity: 0.4; }
`;

export const AdminSetupContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #05010d;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 40px;

  @media (max-width: 600px) {
    padding: 16px;
    align-items: flex-start;
  }

  &::before {
    content: "";
    position: absolute;
    top: -10%;
    left: -10%;
    width: 40%;
    height: 40%;
    background: radial-gradient(
      circle,
      rgba(138, 79, 255, 0.15) 0%,
      transparent 70%
    );
    border-radius: 50%;
    animation: ${pulse} 8s infinite ease-in-out;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -10%;
    right: -10%;
    width: 50%;
    height: 50%;
    background: radial-gradient(
      circle,
      rgba(0, 229, 255, 0.08) 0%,
      transparent 70%
    );
    border-radius: 50%;
    animation: ${pulse} 10s infinite ease-in-out reverse;
  }
`;

export const GlassCard = styled(Box)<BoxProps>`
  background: rgba(13, 2, 33, 0.7);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 32px;
  padding: 32px 48px;
  width: 100%;
  max-width: 1100px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  z-index: 1;
  margin: 40px auto;

  @media (max-width: 1100px) {
    max-width: 95%;
    padding: 32px;
  }

  @media (max-width: 600px) {
    padding: 24px 16px;
    border-radius: 20px;
    margin: 60px 0 20px 0;
  }
`;

export const SectionsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

export const Column = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const HeaderSection = styled(Box)`
  margin-bottom: 24px;
  text-align: center;
`;

export const Title = styled(Typography)`
  font-size: 32px;
  font-weight: 900;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #ffffff 0%, #b388ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

export const Subtitle = styled(Typography)`
  font-size: 15px;
  color: rgba(205, 181, 255, 0.7);
  max-width: 400px;
  margin: 0 auto;
`;

export const SectionTitle = styled(Typography)`
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #b388ff;
  margin: 24px 0 16px 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.9;

  @media (max-width: 600px) {
    font-size: 11px;
    margin: 20px 0 12px 4px;
  }

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, rgba(160, 121, 255, 0.4), transparent);
  }
`;

export const StyledTextField = styled(TextField)`
  margin-bottom: 18px;

  & .MuiOutlinedInput-root {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 16px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    & fieldset {
      border-color: rgba(255, 255, 255, 0.08);
      transition: border-color 0.3s ease;
    }

    &:hover fieldset {
      border-color: rgba(179, 136, 255, 0.3);
    }

    &.Mui-focused {
      background: rgba(255, 255, 255, 0.04);
      box-shadow: 0 0 20px rgba(138, 79, 255, 0.15);

      & fieldset {
        border-color: #a079ff;
        border-width: 1px !important;
      }
    }

    & .MuiInputAdornment-root .MuiSvgIcon-root {
      color: rgba(255, 255, 255, 0.3);
      font-size: 20px;
      transition: color 0.3s ease;
    }

    &.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root {
      color: #a079ff;
    }
  }

  & .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.4);
    font-size: 14px;

    &.Mui-focused {
      color: #a079ff;
    }
  }
`;

export const FormRow = styled(Box)`
  display: flex;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0;
  }

  & > * {
    flex: 1;
  }
`;

export const SwitchContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin: 16px 0 24px 0;
  transition: all 0.3s ease;

  @media (max-width: 600px) {
    padding: 10px 12px;
    margin-bottom: 16px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(179, 136, 255, 0.2);
  }
`;

export const FooterSection = styled(Box)`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

export const SubmitButton = styled(Button)`
  padding: 16px;
  border-radius: 16px;
  font-weight: 800;
  font-size: 16px;
  text-transform: none;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #7c3aed, #a855f7);
  color: #ffffff;
  box-shadow: 0 10px 25px -5px rgba(124, 58, 237, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: linear-gradient(90deg, #6d28d9, #9333ea);
    box-shadow: 0 15px 30px -5px rgba(124, 58, 237, 0.6);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.2);
    box-shadow: none;
  }
`;

export const ErrorState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  animation: ${fadeIn} 0.5s ease-out;

  & .MuiSvgIcon-root {
    font-size: 64px;
    color: #ff5252;
    margin-bottom: 24px;
    opacity: 0.8;
  }
`;

export const BackButtonWrapper = styled(Box)`
  position: absolute;
  top: 32px;
  left: 32px;

  @media (max-width: 600px) {
    top: 16px;
    left: 16px;
  }
`;

export const StyledIconButton = styled("button")`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    transform: translateX(-3px);
  }

  &:focus {
    border-color: #a079ff;
    box-shadow: 0 0 0 2px rgba(138, 79, 255, 0.2);
  }
`;
